import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Truck, RotateCcw, CreditCard, Star, Mail, X } from 'lucide-react';
import { ProductCardGrid } from '../components/ProductCard';
import { products, categories, testimonials } from '../data/products';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export const Home = () => {
  const [email, setEmail] = useState('');
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);

  const bestSellers = products.filter(p => p.badge === 'Best Seller' || p.badge === 'Premium').slice(0, 4);
  const newArrivals = products.filter(p => p.isNew).slice(0, 4);

  const features = [
    { icon: Shield, title: 'Premium Craftsmanship', desc: 'Handcrafted quality' },
    { icon: Truck, title: 'Fast USA Shipping', desc: 'Free over $999' },
    { icon: RotateCcw, title: '30-Day Returns', desc: 'Easy & hassle-free' },
    { icon: CreditCard, title: 'Secure Checkout', desc: '100% protected' },
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Welcome to Bright Vista!', {
        description: 'Your 10% discount code: WELCOME10'
      });
      setEmail('');
      setShowNewsletterPopup(false);
    }
  };

  return (
    <main className="overflow-hidden" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=2669"
            alt="Luxury Living Room"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2D4A3E]/80 via-[#2D4A3E]/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full py-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-4 py-2 bg-[#C67D4E]/20 text-[#C67D4E] text-xs uppercase tracking-[0.2em] font-['Montserrat'] font-semibold mb-6">
              Premium Collection 2026
            </span>
            <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.1] tracking-tight">
              Luxury Furniture Designed for Modern Living
            </h1>
            <p className="font-['Montserrat'] text-base sm:text-lg text-white/80 mt-6 leading-relaxed max-w-xl">
              Discover premium furniture crafted to bring elegance, comfort, and timeless style to your home.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <Link to="/category/all">
                <Button 
                  className="bg-[#C67D4E] text-white hover:bg-[#A86840] px-8 py-6 text-xs uppercase tracking-wider font-semibold rounded-sm transition-all duration-300 font-['Montserrat']"
                  data-testid="hero-shop-btn"
                >
                  Shop Collection
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/category/living-room">
                <Button 
                  variant="outline"
                  className="border-white/50 text-white hover:bg-white hover:text-[#2D4A3E] px-8 py-6 text-xs uppercase tracking-wider font-semibold rounded-sm bg-transparent font-['Montserrat']"
                  data-testid="hero-explore-btn"
                >
                  Explore Furniture
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Hero Features Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-[#FAF8F5]/95 backdrop-blur-md border-t border-[#E8E4DE]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <feature.icon className="text-[#C67D4E] flex-shrink-0" size={22} />
                  <div>
                    <p className="font-['Montserrat'] text-xs sm:text-sm font-semibold text-[#2D4A3E]">{feature.title}</p>
                    <p className="font-['Montserrat'] text-[10px] sm:text-xs text-[#8B7355]">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-20 md:py-28 bg-[#FAF8F5]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              Our Collections
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3">
              Shop by Category
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 8).map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/category/${category.id}`}
                  className="group block relative aspect-[3/4] overflow-hidden"
                  data-testid={`category-${category.id}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2D4A3E]/80 via-[#2D4A3E]/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <h3 className="font-['Cormorant_Garamond'] text-lg md:text-xl text-white">
                      {category.name}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-[10px] md:text-xs uppercase tracking-wider text-[#C67D4E] mt-1 font-['Montserrat'] font-semibold group-hover:gap-2 transition-all">
                      Shop Now <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="py-20 md:py-28 bg-white" data-testid="bestsellers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16"
          >
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
                Customer Favorites
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3">
                Best Selling Furniture
              </h2>
            </div>
            <Link 
              to="/category/all"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#2D4A3E] font-['Montserrat'] font-semibold hover:text-[#C67D4E] transition-colors"
              data-testid="view-all-bestsellers"
            >
              View All <ArrowRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {bestSellers.map((product, index) => (
              <ProductCardGrid key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="py-20 md:py-28 bg-[#FAF8F5]" data-testid="newarrivals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16"
          >
            <div>
              <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
                Fresh Designs
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3">
                New Arrivals
              </h2>
            </div>
            <Link 
              to="/category/all"
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#2D4A3E] font-['Montserrat'] font-semibold hover:text-[#C67D4E] transition-colors"
              data-testid="view-all-newarrivals"
            >
              View All <ArrowRight size={14} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {newArrivals.map((product, index) => (
              <ProductCardGrid key={product.id} product={product} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-[#2D4A3E]" data-testid="whychoose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              The Bright Vista Difference
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-white mt-3">
              Why Customers Choose Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {[
              { icon: Shield, title: 'Premium Quality', desc: 'Finest materials sourced globally' },
              { icon: Star, title: 'Elegant Designs', desc: 'Timeless aesthetics' },
              { icon: Truck, title: 'Fast Shipping', desc: 'Free over $999' },
              { icon: RotateCcw, title: '30-Day Returns', desc: 'Hassle-free policy' },
              { icon: CreditCard, title: 'Secure Checkout', desc: '100% protected' },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto bg-[#C67D4E]/20 rounded-full flex items-center justify-center mb-4">
                  <item.icon className="text-[#C67D4E]" size={24} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-lg text-white mb-1">{item.title}</h3>
                <p className="font-['Montserrat'] text-xs text-[#A8B5AB]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-[#C67D4E] font-['Montserrat'] font-semibold">
              Customer Love
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E] mt-3">
              What Our Customers Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#FAF8F5] p-6 md:p-8 border border-[#E8E4DE]"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#C67D4E] text-[#C67D4E]" />
                  ))}
                </div>
                <p className="font-['Cormorant_Garamond'] text-lg italic text-[#5A6B5D] mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-['Montserrat'] font-semibold text-sm text-[#2D4A3E]">{testimonial.name}</p>
                  <p className="font-['Montserrat'] text-xs text-[#8B7355]">{testimonial.location}</p>
                  <p className="font-['Montserrat'] text-[10px] text-[#C67D4E] mt-1">Purchased: {testimonial.product}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 md:py-28 bg-[#FAF8F5]" data-testid="newsletter-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-14 h-14 mx-auto bg-[#C67D4E]/20 rounded-full flex items-center justify-center mb-6">
                <Mail className="text-[#C67D4E]" size={24} />
              </div>
              <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-[#2D4A3E]">
                Get 10% Off Your First Order
              </h2>
              <p className="font-['Montserrat'] text-base text-[#5A6B5D] mt-4 mb-8">
                Join our newsletter for exclusive offers and interior design inspiration.
              </p>
              <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-white border border-[#E8E4DE] rounded-sm py-3 px-4 text-[#2D4A3E] placeholder-[#8B7355] focus:outline-none focus:border-[#2D4A3E] font-['Montserrat']"
                  data-testid="newsletter-email"
                  required
                />
                <Button 
                  type="submit"
                  className="bg-[#2D4A3E] text-white hover:bg-[#1E332A] px-8 py-3 rounded-sm text-xs uppercase tracking-wider font-semibold font-['Montserrat']"
                  data-testid="newsletter-submit"
                >
                  Subscribe
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Free Shipping Banner */}
      <div className="bg-[#C67D4E] py-4" data-testid="shipping-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <p className="text-center font-['Montserrat'] text-xs sm:text-sm text-white font-semibold uppercase tracking-wider">
            <Truck className="inline-block mr-2 -mt-0.5" size={16} />
            Free Shipping on Orders Over $999 | Fast Delivery Across USA
          </p>
        </div>
      </div>

      {/* Exit Intent Popup */}
      {showNewsletterPopup && (
        <div className="fixed inset-0 bg-[#2D4A3E]/60 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#FAF8F5] max-w-md w-full p-8 relative"
          >
            <button
              onClick={() => setShowNewsletterPopup(false)}
              className="absolute top-4 right-4 text-[#8B7355] hover:text-[#2D4A3E]"
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#2D4A3E] mb-2">
                Wait! Don't Leave Yet
              </h3>
              <p className="font-['Montserrat'] text-sm text-[#5A6B5D] mb-6">
                Get 10% off your first order when you subscribe
              </p>
              <form onSubmit={handleNewsletterSubmit}>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-[#E8E4DE] rounded-sm py-3 mb-4"
                  required
                />
                <Button 
                  type="submit"
                  className="w-full bg-[#C67D4E] text-white hover:bg-[#A86840] py-3 rounded-sm text-xs uppercase tracking-wider font-semibold"
                >
                  Get My 10% Off
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
};
